import {
    AVATAR_API_BASE_URL,
    ICON_API_BASE_URL,
    getStockPhotoUrl,
} from "@/apps/page/services/page.service";
import gridPattern from "@/views/blocks/long-form/grid-pattern.svg";
import {
    Centered,
    CenteredProps,
    SplitWithImage,
    SplitWithImageProps,
    TwoColumnsWithScreenshot,
    TwoColumnsWithScreenshotProps,
    WithImageTitles,
    WithImageTitlesProps,
    WithStickyProductScreenshot,
    WithStickyProductScreenshotProps,
    WithTestimonial,
    WithTestimonialProps,
} from "@/views/blocks/long-form/long-form";

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { BlockDef } from "./block.interfaces";
import {
    arrayField,
    backgroundImageField,
    buttonField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import { useFieldConfig } from "./useFieldConfig";

export function useLongForms() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const longFormBlock = computed<BlockDef>(() =>
        createBlock<WithStickyProductScreenshotProps>({
            typeId: "WithStickyProductScreenshot",
            type: "longform",
            name: t("page.blocks.longform.withStickyProductScreenshot"),
            component: WithStickyProductScreenshot,
        })
            .withField(
                "backgroundImage",
                backgroundImageField(
                    translations.value.backgroundImage,
                    {
                        src: gridPattern,
                        transparency: 4,
                    },
                    {
                        src: translations.value.src,
                        transparency: translations.value.backgroundImageTransparency,
                    }
                )
            )
            .withField(
                "tag",
                headingField(translations.value.tag, "Streamline Operations", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Experience a revolutionary approach to business management with our integrated platform. Streamline operations, enhance productivity, and drive growth with powerful tools designed for modern enterprises.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getStockPhotoUrl(
                            "licences-saas",
                            "teamwork-programmer-working-with-computer"
                        ),
                        alt: "Business transformation platform",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withField(
                "topText",
                htmlField(
                    translations.value.topText,
                    "In today's fast-paced business environment, staying competitive requires efficient tools and streamlined processes. Our platform integrates seamlessly with your existing workflows, providing powerful automation capabilities and real-time insights. Transform the way your team collaborates and delivers results with our comprehensive solution.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/general/zap.svg`,
                            title: "Smart Automation.",
                            description:
                                "Automate repetitive tasks and workflows with intelligent systems that learn and adapt to your business needs. Save time and reduce errors while increasing productivity.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/charts/line-chart-up-01.svg`,
                            title: "Real-time Analytics.",
                            description:
                                "Access comprehensive business insights through intuitive dashboards. Make data-driven decisions with powerful analytics tools that track performance in real-time.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/dataflow-01.svg`,
                            title: "Seamless Integration.",
                            description:
                                "Connect with your favorite tools and services effortlessly. Our platform works harmoniously with your existing tech stack, ensuring smooth operations.",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New feature",
                        description: "New description",
                    }
                )
            )
            .withField(
                "bottomText",
                htmlField(
                    translations.value.bottomText,
                    "Our platform adapts to your unique business needs, providing customizable solutions that grow with your organization. From startups to enterprise-level operations, we offer scalable features that ensure optimal performance at every stage of your business journey.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryTitle",
                headingField(translations.value.secondaryTitle, "Built for Modern Business", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "secondaryDescription",
                htmlField(
                    translations.value.secondaryDescription,
                    "Experience enterprise-grade capabilities without the complexity. Our cloud-based platform delivers powerful features through an intuitive interface, making advanced business tools accessible to teams of all sizes. Whether you're scaling up or optimizing operations, we provide the foundation for your success.",
                    translations.value.text
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const centeredLongFormBlock = computed<BlockDef>(() =>
        createBlock<CenteredProps>({
            typeId: "Centered",
            type: "longform",
            name: t("page.blocks.longform.centered"),
            component: Centered,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Overview", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Python for Data Science", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Master essential Python skills for data analysis, visualization, and machine learning with our comprehensive course for beginners and intermediate programmers. Learn foundational concepts and advanced techniques to excel in the data science field.",
                    translations.value.text
                )
            )
            .withField(
                "topText",
                htmlField(
                    translations.value.topText,
                    "Our curriculum covers all the critical components you need to excel in data science, with hands-on exercises and real-world applications:",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/code-01.svg`,
                            title: "Libraries:",
                            description:
                                "Master NumPy, Pandas, Matplotlib, Seaborn, and Scikit-learn for data science fundamentals.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/charts/line-chart-up-01.svg`,
                            title: "Analysis:",
                            description:
                                "Learn data cleaning, transformation, statistical methods, and visualization techniques.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/cpu-chip-01.svg`,
                            title: "Models:",
                            description:
                                "Build regression, decision trees, random forests, SVMs, clustering, and neural network models.",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New capability",
                        description: "Enhanced functionality description",
                    }
                )
            )
            .withField(
                "bottomText",
                htmlField(
                    translations.value.bottomText,
                    "Upon completion, you'll have the skills to analyze complex datasets, create insightful visualizations, and build predictive models for real-world applications. You'll also understand how to evaluate model performance, tune hyperparameters, and deploy your solutions in production environments.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryTitle",
                headingField(
                    translations.value.secondaryTitle,
                    "Complete data science toolkit in 4 weeks",
                    "h3",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "secondaryDescriptionOne",
                htmlField(
                    translations.value.secondaryDescriptionOne,
                    "Our hands-on approach ensures you gain practical experience with each concept through real-world projects and guided exercises. From day one, you'll be working with actual datasets and solving problems that data scientists encounter in their daily work.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryDescriptionTwo",
                htmlField(
                    translations.value.secondaryDescriptionTwo,
                    "Students receive access to cloud computing resources, dataset libraries, and industry-standard tools used by professional data scientists. Our virtual lab environment allows you to experiment with large datasets without worrying about hardware limitations or complex setup procedures.",
                    translations.value.text
                )
            )
            .withField("testimonial", {
                label: translations.value.testimonial,
                group: "texts",
                fields: [
                    {
                        value: "text",
                        type: "html",
                        label: translations.value.text,
                        rows: 3,
                    },
                    {
                        value: "personImageSrc",
                        type: "image",
                        label: translations.value.iconSrc,
                    },
                    {
                        value: "personName",
                        type: "text",
                        label: translations.value.name,
                    },
                    {
                        value: "personPosition",
                        type: "text",
                        label: translations.value.role,
                    },
                ],
                defaultData: {
                    data: {
                        text: "This course transformed my career path completely. The practical projects helped me build a portfolio that landed me a job as a junior data analyst. The instructors were incredibly supportive and the community of learners provided valuable feedback on my work.",
                        personImageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                        personName: "David Chen",
                        personPosition: "Data Analyst",
                    },
                    isHidden: false,
                },
            })
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.src,
                    {
                        src: getStockPhotoUrl(
                            "courses",
                            "cheerful-young-woman-glasses-front-laptop"
                        ),
                        alt: "Python data visualization dashboard",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withField(
                "infoNote",
                htmlField(
                    translations.value.infoNote,
                    "Certificate of completion recognized by leading tech companies",
                    translations.value.text
                )
            )
            .withField(
                "tertiaryTitle",
                headingField(
                    translations.value.tertiaryTitle,
                    "Comprehensive learning experience",
                    "h3",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "tertiaryDescriptionOne",
                htmlField(
                    translations.value.tertiaryDescriptionOne,
                    "The program includes 15 hands-on projects, 8 case studies, and mentorship sessions with experienced data scientists working in the industry. Each project is designed to reinforce specific skills while building toward a comprehensive portfolio that demonstrates your capabilities to potential employers.",
                    translations.value.text
                )
            )
            .withField(
                "tertiaryDescriptionTwo",
                htmlField(
                    translations.value.tertiaryDescriptionTwo,
                    "Our active learning community provides networking opportunities, code reviews, and collaborative projects to enhance your professional development. Weekly office hours give you direct access to instructors for personalized guidance, and our career services team helps you leverage your new skills in the job market.",
                    translations.value.text
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const twoColumnsWithScreenshotBlock = computed<BlockDef>(() =>
        createBlock<TwoColumnsWithScreenshotProps>({
            typeId: "TwoColumnsWithScreenshot",
            type: "longform",
            name: t("page.blocks.longform.twoColumnsWithScreenshot"),
            component: TwoColumnsWithScreenshot,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Streamlined Process", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Accelerate Your Development", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withArrayField(
                "paragraphs",
                arrayField(
                    translations.value.paragraph,
                    "texts",
                    {
                        text: {
                            label: translations.value.text,
                            type: "html",
                        },
                    },
                    [
                        {
                            text: "Our platform reduces development cycles by automating repetitive tasks and streamlining workflows. Teams can focus on innovation rather than maintenance, resulting in faster time-to-market and improved product quality across all stages of development.",
                        },
                        {
                            text: "Advanced CI/CD pipelines integrate seamlessly with your existing tools, enabling continuous deployment with confidence. Automated testing and quality assurance processes ensure that every release meets your standards without manual intervention.",
                        },
                        {
                            text: "Real-time collaboration features allow developers, designers, and stakeholders to work together efficiently regardless of location. Our intuitive interface makes it easy to track progress, identify bottlenecks, and allocate resources effectively.",
                        },
                        {
                            text: "Comprehensive analytics provide insights into your development process, helping you identify opportunities for optimization. With customizable dashboards and reporting tools, you can measure performance metrics that matter most to your organization and make data-driven decisions.",
                        },
                    ],
                    {
                        text: "New paragraph text",
                    }
                )
            )
            .withField(
                "screenshot",
                descriptiveImageField(
                    translations.value.image,
                    {
                        src: getStockPhotoUrl(
                            "licences-saas",
                            "cheerful-web-developers-working-laptop"
                        ),
                        alt: "Screenshot of deployment workflow",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withImageTitlesBlock = computed<BlockDef>(() =>
        createBlock<WithImageTitlesProps>({
            typeId: "WithImageTitles",
            type: "longform",
            name: t("page.blocks.longform.withImageTitles"),
            component: WithImageTitles,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Our mission", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Building the future of distributed workforces",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We're revolutionizing how teams work together in the digital age. Our platform enables seamless collaboration across borders, time zones, and departments, empowering organizations to achieve their full potential through innovative technology solutions.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryTitle",
                headingField(translations.value.secondaryTitle, "Our vision", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "secondaryDescriptionOne",
                htmlField(
                    translations.value.secondaryDescriptionOne,
                    "We envision a world where geographical boundaries no longer limit business potential. Our platform breaks down traditional barriers to collaboration, enabling teams to work together as if they were in the same room, regardless of their physical location.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryDescriptionTwo",
                htmlField(
                    translations.value.secondaryDescriptionTwo,
                    "Our commitment to innovation drives everything we do. We continuously evolve our platform to meet the changing needs of modern businesses, ensuring that our users always have access to the most advanced tools and features.",
                    translations.value.text
                )
            )
            .withArrayField(
                "images",
                arrayField(
                    translations.value.image,
                    "images",
                    {
                        src: {
                            label: translations.value.src,
                            type: "image",
                        },
                        alt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                    },
                    [
                        {
                            src: getStockPhotoUrl(
                                "communities",
                                "close-up-happy-people-posing-together"
                            ),
                            alt: "Team collaborating around a table",
                        },
                        {
                            src: getStockPhotoUrl(
                                "webinars",
                                "business-team-negotiating-with-partners"
                            ),
                            alt: "Team discussion in meeting room",
                        },
                        {
                            src: getStockPhotoUrl(
                                "licences-saas",
                                "portrait-male-programmer-office"
                            ),
                            alt: "Open office workspace environment",
                        },
                        {
                            src: getStockPhotoUrl(
                                "webinars",
                                "team-meeting-online-conference-call-laptop"
                            ),
                            alt: "Remote team video conference",
                        },
                    ],
                    {
                        src: getStockPhotoUrl(
                            "communities",
                            "networking-concept-still-life-arrangement"
                        ),
                        alt: "New image",
                    }
                )
            )
            .withField(
                "statsTitle",
                headingField(translations.value.statsTitle, "Our stats", "h4", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        { title: "$12.5M", description: "Total Funding" },
                        { title: "3.2K", description: "Global Clients" },
                        { title: "250", description: "Projects Completed" },
                        { title: "4.5M", description: "User Interactions" },
                    ],
                    {
                        title: "New stat",
                        description: "New description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withTestimonialBlock = computed<BlockDef>(() =>
        createBlock<WithTestimonialProps>({
            typeId: "WithTestimonial",
            type: "longform",
            name: t("page.blocks.longform.withTestimonial"),
            component: WithTestimonial,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Streamline Operations", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Experience a revolutionary approach to business management with our integrated platform. Streamline operations, enhance productivity, and drive growth with powerful tools designed for modern enterprises.",
                    translations.value.text
                )
            )
            .withField(
                "topText",
                htmlField(
                    translations.value.topText,
                    "In today's fast-paced business environment, staying competitive requires efficient tools and streamlined processes. Our platform integrates seamlessly with your existing workflows, providing powerful automation capabilities and real-time insights. Transform the way your team collaborates and delivers results with our comprehensive solution.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/general/zap.svg`,
                            title: "Smart Automation.",
                            description:
                                "Automate repetitive tasks and workflows with intelligent systems that learn and adapt to your business needs. Save time and reduce errors while increasing productivity.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/charts/line-chart-up-01.svg`,
                            title: "Real-time Analytics.",
                            description:
                                "Access comprehensive business insights through intuitive dashboards. Make data-driven decisions with powerful analytics tools that track performance in real-time.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/dataflow-01.svg`,
                            title: "Seamless Integration.",
                            description:
                                "Connect with your favorite tools and services effortlessly. Our platform works harmoniously with your existing tech stack, ensuring smooth operations.",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New feature",
                        description: "New description",
                    }
                )
            )
            .withField(
                "bottomText",
                htmlField(
                    translations.value.bottomText,
                    "Our platform adapts to your unique business needs, providing customizable solutions that grow with your organization. From startups to enterprise-level operations, we offer scalable features that ensure optimal performance at every stage of your business journey.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryTitle",
                headingField(translations.value.secondaryTitle, "Built for Modern Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "secondaryDescription",
                htmlField(
                    translations.value.secondaryDescription,
                    "Experience enterprise-grade capabilities without the complexity. Our cloud-based platform delivers powerful features through an intuitive interface, making advanced business tools accessible to teams of all sizes. Whether you're scaling up or optimizing operations, we provide the foundation for your success.",
                    translations.value.text
                )
            )
            .withField("testimonial", {
                label: translations.value.testimonial,
                group: "texts",
                fields: [
                    {
                        value: "text",
                        type: "html",
                        label: translations.value.text,
                        rows: 3,
                    },
                    {
                        value: "personImageSrc",
                        type: "image",
                        label: translations.value.iconSrc,
                    },
                    {
                        value: "personName",
                        type: "text",
                        label: translations.value.name,
                    },
                    {
                        value: "personPosition",
                        type: "text",
                        label: translations.value.role,
                    },
                ],
                defaultData: {
                    data: {
                        text: "This platform has transformed our workflow, improving efficiency by 50% and enhancing collaboration. The intuitive interface and analytics have provided valuable insights, enabling better strategic decisions.",
                        personImageSrc: `${AVATAR_API_BASE_URL}/f/1`,
                        personName: "Jane Doe",
                        personPosition: "CEO at Company Inc.",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const splitWithImageBlock = computed<BlockDef>(() =>
        createBlock<SplitWithImageProps>({
            typeId: "SplitWithImage",
            type: "longform",
            name: t("page.blocks.longform.splitWithImage"),
            component: SplitWithImage,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Streamline Operations", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(translations.value.title, "Transform Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "Experience a revolutionary approach to business management with our integrated platform. Streamline operations, enhance productivity, and drive growth with powerful tools designed for modern enterprises.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.image,
                    {
                        src: getStockPhotoUrl("communities", "diverse-friends-gathering-together"),
                        alt: "Team collaborating in office",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withField(
                "topText",
                htmlField(
                    translations.value.topText,
                    "In today's fast-paced business environment, staying competitive requires efficient tools and streamlined processes. Our platform integrates seamlessly with your existing workflows, providing powerful automation capabilities and real-time insights. Transform the way your team collaborates and delivers results with our comprehensive solution.",
                    translations.value.text
                )
            )
            .withArrayField(
                "features",
                arrayField(
                    translations.value.feature,
                    "features",
                    {
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/general/zap.svg`,
                            title: "Smart Automation.",
                            description:
                                "Automate repetitive tasks and workflows with intelligent systems that learn and adapt to your business needs. Save time and reduce errors while increasing productivity.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/charts/line-chart-up-01.svg`,
                            title: "Real-time Analytics.",
                            description:
                                "Access comprehensive business insights through intuitive dashboards. Make data-driven decisions with powerful analytics tools that track performance in real-time.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/dataflow-01.svg`,
                            title: "Seamless Integration.",
                            description:
                                "Connect with your favorite tools and services effortlessly. Our platform works harmoniously with your existing tech stack, ensuring smooth operations.",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New feature",
                        description: "New description",
                    }
                )
            )
            .withField(
                "bottomText",
                htmlField(
                    translations.value.bottomText,
                    "Our platform adapts to your unique business needs, providing customizable solutions that grow with your organization. From startups to enterprise-level operations, we offer scalable features that ensure optimal performance at every stage of your business journey.",
                    translations.value.text
                )
            )
            .withField(
                "secondaryTitle",
                headingField(translations.value.secondaryTitle, "Built for Modern Business", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "secondaryDescription",
                htmlField(
                    translations.value.secondaryDescription,
                    "Experience enterprise-grade capabilities without the complexity. Our cloud-based platform delivers powerful features through an intuitive interface, making advanced business tools accessible to teams of all sizes. Whether you're scaling up or optimizing operations, we provide the foundation for your success.",
                    translations.value.text
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            longFormBlock.value,
            centeredLongFormBlock.value,
            twoColumnsWithScreenshotBlock.value,
            withImageTitlesBlock.value,
            withTestimonialBlock.value,
            splitWithImageBlock.value,
        ]),
    };
}
